import React, { useState } from 'react';
import moment from 'moment';
import lodash from 'lodash';
import { useMutation } from '@apollo/client';
import { Field, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Form as FormSemantic } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorHandler } from '@playbookux/playbook-web-core';
import Dropdown from './dropdown';
import {
  ButtonSave,
  ContainerButton,
  Modal,
  ErrorContainer,
  SmallText,
  InLineContainer,
  Checkbox,
} from './styles';

import {
  CREATE_USAGE_LOG,
} from '../graphql';
import schema from './schemas';

const Entry = (props) => {
  const {
    onClose,
    refetch,
    company,
  } = props;

  const [selectedProject, setSelectedProject] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [signal, setSignal] = useState('plus');
  const [errorMessage, setErrorMsg] = useState(null);

  const [createEntry, { loading: loadingEntry }] = useMutation(CREATE_USAGE_LOG);

  const handleCloseModal = () => {
    onClose();
  };

  const handleKeyPress = (event) => {
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);
    if (!/^\d+$/.test(keyValue)) {
      event.preventDefault();
    }
  };

  const handleEntries = async (res, companyId, costSignal) => {
    setErrorMsg(null);
    try {
      const cost = costSignal === 'plus' ? parseFloat(res.cost) : parseFloat(`-${res.cost}`);
      await createEntry({
        variables: {
          projectId: res.project,
          companyId,
          cost,
          reason: res.reason,
          createdAt: moment(res.date),
          updatedAt: moment(res.date),
        },
      });
      handleCloseModal();
      refetch();
    } catch (err) {
      const graphQLError = lodash.first(err.graphQLErrors);
      setErrorMsg(graphQLError);
    }
  };

  return (
    <Modal
      closeOnEscape
      closeOnDimmerClick
      open
      onClose={onClose}
    >
      <Formik
        initialValues={{
          date: startDate,
          project: selectedProject,
          cost: null,
          reason: '',
        }}
        onSubmit={({ ...res }) => handleEntries(res, company, signal)}
        validationSchema={schema}
      >
        {({
          errors,
          handleSubmit,
          setFieldValue,
          values,
        }) => (
          <FormSemantic onSubmit={handleSubmit}>
            <FormSemantic.Field required className={!startDate ? 'error' : ''}>
              <SmallText>Date</SmallText>
              <DatePicker
                name="date"
                selected={startDate}
                showTimeSelect
                onChange={(date) => {
                  setFieldValue('date', date, true);
                  setStartDate(date);
                }}
                dateFormat="Pp"
              />
            </FormSemantic.Field>
            <FormSemantic.Field required className={selectedProject.length < 1 ? 'error' : ''}>
              <SmallText>Project Name</SmallText>
              <Dropdown
                company={company}
                selectedProject={selectedProject}
                setProject={setSelectedProject}
                setFieldValue={setFieldValue}
              />
            </FormSemantic.Field>
            <FormSemantic.Field required className={!values.cost ? 'error' : ''}>
              <SmallText>Cost</SmallText>
              <InLineContainer>
                <Checkbox
                  radio
                  label="+"
                  name="checkboxRadioGroup"
                  value="plus"
                  checked={signal === 'plus'}
                  onChange={(e, data) => setSignal(data.value)}
                />
                <Checkbox
                  radio
                  label="-"
                  name="checkboxRadioGroup"
                  value="minus"
                  checked={signal === 'minus'}
                  onChange={(e, data) => setSignal(data.value)}
                />
                <Field
                  name="cost"
                  error={errors.cost}
                  onKeyPress={handleKeyPress}
                />
              </InLineContainer>
            </FormSemantic.Field>
            <FormSemantic.Field required className={!values.reason ? 'error' : ''}>
              <SmallText>Reason</SmallText>
              <Field
                name="reason"
                error={errors.reason}
              />
            </FormSemantic.Field>
            <ContainerButton>
              <ErrorContainer>
                { errorMessage && (
                  <ErrorHandler
                    error={errorMessage}
                    defaultMessage="Error while creating a new entry"
                  />
                )}
              </ErrorContainer>

              <ButtonSave
                loading={loadingEntry}
                type="submit"
              >
                Save
              </ButtonSave>
            </ContainerButton>
          </FormSemantic>
        )}
      </Formik>
    </Modal>
  );
};

Entry.propTypes = {
  errors: PropTypes.shape({
    name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  values: PropTypes.shape({
    date: PropTypes.string,
    project: PropTypes.string,
    cost: PropTypes.number,
    reason: PropTypes.string,
  }),
  refetch: PropTypes.func.isRequired,
  company: PropTypes.string.isRequired,
};

Entry.defaultProps = {
  values: {
    date: null,
    project: '',
    cost: null,
    reason: '',
  },
  errors: {},
};

export default Entry;
